/**
 * Water is failure: the body freezes for the fail pause and respawns at the
 * last respawn point passed. Restart respawns at once.
 */

import { capsuleHeight, probeGround } from "../collision";
import type { EventBuffer } from "../events";
import { linkOnFail } from "../link";
import type { SimState } from "../state";
import type { CompiledTuning } from "../tuning";
import { Ev, S } from "../types";
import type { World } from "../world";
import { setState } from "./transitions";

export function checkWater(world: World, s: SimState, t: CompiledTuning, ev: EventBuffer): boolean {
  if (s.state === S.Fail) return false;
  const r = t.capsuleRadius;
  if (!world.overlapWater(s.x - r, s.y, s.x + r, s.y + capsuleHeight(t, s.tuck))) return false;
  s.vx = 0;
  s.vy = 0;
  s.ax = 0;
  s.ay = 0;
  s.omega = 0;
  s.phiDot = 0;
  s.barId = -1;
  s.ladderId = -1;
  s.ladderRung = 0;
  s.ladderChain = 0;
  s.popping = 0;
  s.ladderLiftY = 0;
  s.ladderLiftTick = 0;
  s.ladderLiftVelocity = 0;
  s.ladderCrossings = 0;
  s.ladderDuckBottom = -1;
  s.ledgeSolid = -1;
  s.grounded = 0;
  s.failTicks = t.failPauseTicks;
  ev.push(Ev.Failed, s.tick);
  linkOnFail(s, ev);
  setState(s, ev, S.Fail);
  return true;
}

export function respawn(world: World, s: SimState, t: CompiledTuning, ev: EventBuffer): void {
  const i = s.respawnIndex < world.respawnCount ? s.respawnIndex : 0;
  s.x = world.respawnX[i]!;
  s.y = world.respawnY[i]!;
  s.vx = 0;
  s.vy = 0;
  s.ax = 0;
  s.ay = 0;
  s.phi = 0;
  s.phiDot = 0;
  s.barLean = 0;
  s.tumbling = 0;
  s.rollTurn = 0;
  s.plantBox = -1;
  s.planted = 0;
  s.plantHands = 0;
  s.mantling = 0;
  s.vaultArmBox = -1;
  s.slideCoyote = 0;
  s.slideBoostPipe = -1;
  s.slideBoostReady = 0;
  s.slideBoostDir = 1;
  s.ledgeSolid = -1;
  s.lastLedge = -1;
  s.ledgeLockout = 0;
  s.grounded = 0;
  s.coyote = 0;
  s.buffer = 0;
  s.jumpCutArmed = 0;
  s.barId = -1;
  s.ladderId = -1;
  s.ladderRung = 0;
  s.ladderChain = 0;
  s.popping = 0;
  s.ladderLiftY = 0;
  s.ladderLiftTick = 0;
  s.ladderLiftVelocity = 0;
  s.ladderCrossings = 0;
  s.ladderDuckBottom = -1;
  s.theta = 0;
  s.omega = 0;
  s.attachLeft = 0;
  s.lastBarId = -1;
  s.lockout = 0;
  s.leftRadius = 1;
  s.failTicks = 0;
  s.armsOutTicks = 0;
  s.hangTicks = 0;
  s.linkOpen = 0;
  s.ticksSinceLand = 1e9;
  s.ticksSinceRelease = 1e9;
  s.facing = 1;
  s.grounded = probeGround(world, s, t, 0.02) ? 1 : 0;
  s.apexY = s.y;
  if (s.grounded === 1) s.coyote = t.coyoteTicks;
  let m = 0;
  while (m < world.markCount && world.markX[m]! <= s.x) m++;
  s.markIndex = m;
  ev.push(Ev.Respawned, s.tick, i);
  setState(s, ev, S.Idle);
}

/** Advance the respawn index and emit mark events as the character moves right. */
export function trackProgress(world: World, s: SimState, ev: EventBuffer): void {
  while (s.respawnIndex + 1 < world.respawnCount && s.x >= world.respawnX[s.respawnIndex + 1]!) {
    s.respawnIndex++;
  }
  while (s.markIndex < world.markCount && s.x >= world.markX[s.markIndex]!) {
    ev.push(Ev.MarkPassed, s.tick, s.markIndex);
    s.markIndex++;
  }
}
